import React from 'react'
import { connect, useSelector } from 'react-redux'

import { LotInterface } from '@src/api/houseAndLand'
import usePropertyCompareFirebaseTrigger from '@src/components/compare-property/utils'

import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import BackButton from '@pages/properties-view/house-and-land/v2/lots-side-panel/back-button'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import {
  CloseSvg,
  CompareSvg,
  Eye3SlashSvg,
  Eye3Svg,
  FullScreenSvg,
  PanoramaSvg,
  Pin3Svg,
  PlaySvg,
  StopCompareSvg,
  StopSvg,
  Unpin3Svg,
} from '@svg/react'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { LotProperty, PropertyType } from '@adUtilities/types'

interface ComponentProps {
  lot: LotInterface
  projectIdentity: ProjectIdentity
  hasPanoramicData: boolean
  fullScreenToggle: boolean
  setFullScreenToggle: (arg: boolean) => void
  packageInfoToggle: boolean
  setPackageInfoToggle: (arg: boolean) => void
  slideShowToggle: boolean
  setSlideShowToggle: (arg: boolean) => void
  isVideoPlaying: boolean
  shouldShowSlideshowBtn: boolean
  isPinned: boolean
  togglePin: () => void
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  hideCompareAction?: boolean
  hidePropertyControls?: boolean
}

function Controls({
  lot,
  projectIdentity,
  hasPanoramicData,
  fullScreenToggle,
  setFullScreenToggle,
  packageInfoToggle,
  setPackageInfoToggle,
  slideShowToggle,
  setSlideShowToggle,
  isVideoPlaying,
  shouldShowSlideshowBtn,
  isPinned,
  togglePin,
  comparePropertyProps,
  hideCompareAction,
  hidePropertyControls,
}: ComponentProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const activePrecinctId = useSelector(
    (state: RootStateTypeExtra) =>
      state.firestore.session?.houseAndLand?.activePrecinctId
  )
  const activeStageId = useSelector(
    (state: RootStateTypeExtra) =>
      state.firestore.session?.houseAndLand?.activeStageId
  )

  const handleNavigateToPanoramic = React.useCallback(() => {
    firebaseControlQuery.updateRoute('panoramic')
  }, [])

  const {
    checkPropertyComparability,
    compareModeIsActive,
    setInitialPropertyAtComparisonStart,
  } = comparePropertyProps
  const { triggerComparisonStart, triggerComparisonStop } =
    usePropertyCompareFirebaseTrigger({ comparePropertyProps })

  const isComparable = React.useMemo<boolean>(
    () =>
      !hideCompareAction &&
      checkPropertyComparability({
        propertyType: PropertyType.Lot,
        data: lot,
      }),
    [lot, checkPropertyComparability, hideCompareAction]
  )

  const handleToggleCompareMode = React.useCallback(() => {
    if (!(activePrecinctId && activeStageId)) {
      return
    }
    if (!compareModeIsActive) {
      const property: LotProperty = {
        propertyType: PropertyType.Lot,
        data: lot,
        references: {
          precinctId: activePrecinctId,
          stageId: activeStageId,
        },
      }
      triggerComparisonStart({
        property,
      })
      setInitialPropertyAtComparisonStart(property)
      setFullScreenToggle(false)
      return
    }
    triggerComparisonStop()
  }, [
    compareModeIsActive,
    triggerComparisonStart,
    triggerComparisonStop,
    lot,
    activePrecinctId,
    activeStageId,
    setFullScreenToggle,
    setInitialPropertyAtComparisonStart,
  ])

  const handleContainerClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
    },
    []
  )

  return (
    <div
      className={`flex flex-col gap-[14px] text-white ${isVideoPlaying && 'hidden'}`}
      onClick={handleContainerClick}
      role="none"
    >
      {fullScreenToggle && (
        <BackButton
          className="rounded bg-[#747474] p-1.5"
          iconClasses="h-5 w-5 stroke-white !scale-[1] !rotate-180"
        />
      )}

      <button
        type="button"
        onClick={() => setFullScreenToggle(!fullScreenToggle)}
        className="rounded bg-[#747474] p-1.5"
      >
        {fullScreenToggle ? (
          <CloseSvg className="h-5 w-5 !scale-[.8] text-white" />
        ) : (
          <FullScreenSvg className="h-5 w-5 text-white" />
        )}
      </button>
      {hidePropertyControls ? null : (
        <>
          {!slideShowToggle && (
            <>
              {!compareModeIsActive && (
                <button
                  type="button"
                  className="rounded bg-[#747474] p-1.5"
                  onClick={togglePin}
                >
                  {isPinned ? (
                    <Unpin3Svg className="h-5 w-5" />
                  ) : (
                    <Pin3Svg className="h-5 w-5" />
                  )}
                </button>
              )}

              <button
                type="button"
                onClick={() => setPackageInfoToggle(!packageInfoToggle)}
                className="rounded bg-[#747474] p-1.5"
              >
                {packageInfoToggle ? (
                  <Eye3SlashSvg className="h-5 w-5 stroke-white" />
                ) : (
                  <Eye3Svg className="h-5 w-5 stroke-white" />
                )}
              </button>
              {isComparable && !slideShowToggle ? (
                <button
                  type="button"
                  onClick={handleToggleCompareMode}
                  className="rounded bg-[#747474] p-1.5"
                >
                  {compareModeIsActive ? (
                    <StopCompareSvg
                      className="h-5 w-5 !scale-[.9] fill-white stroke-white"
                      stroke={1.6}
                    />
                  ) : (
                    <CompareSvg
                      className="h-5 w-5 !scale-[.9] stroke-white"
                      stroke={1.6}
                    />
                  )}
                </button>
              ) : null}
              {hasPanoramicData && !compareModeIsActive && (
                <button
                  className="rounded bg-[#747474] p-1.5"
                  type="button"
                  onClick={handleNavigateToPanoramic}
                >
                  <PanoramaSvg className="h-5 w-5 !scale-[.9] text-white" />
                </button>
              )}
            </>
          )}
          {shouldShowSlideshowBtn && !compareModeIsActive && (
            <button
              type="button"
              onClick={() => setSlideShowToggle(!slideShowToggle)}
              className={`rounded bg-[#747474] ${!slideShowToggle ? 'p-2' : 'p-1.5'}`}
            >
              {!slideShowToggle ? (
                <PlaySvg className="h-4 w-4" />
              ) : (
                <StopSvg className="h-5 w-5" />
              )}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default connect(({ projectIdentity }: RootStateTypeExtra) => ({
  projectIdentity,
}))(Controls)
